/**
 * /partner — the affiliate platform, all on one route:
 *  - a high-conversion landing (hero, live metrics, how-it-works, features,
 *    commission levels, FAQ) for visitors,
 *  - the partner panel (links, postbacks, per-SubID stats) rendered inline on
 *    the same page once a TON wallet is connected & the user is a participant.
 *
 * Incoming `?ref=<key>` marks the visitor as invited by that partner; the
 * "buy a ticket" CTA carries the key into the main app so the inviter is
 * attached on-chain with the first purchase.
 */
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TonConnectButton, useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import {
  BarChart3,
  Check,
  ChevronDown,
  Code2,
  Handshake,
  Layers,
  LayoutDashboard,
  Link2,
  Loader2,
  QrCode,
  Rocket,
  Send,
  ShieldCheck,
  Ticket,
  Wallet,
  Webhook,
  Zap,
} from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LegalLinks } from '../components/LegalLinks';
import { PyramidSpinner } from '../components/PyramidSpinner';
import { WalletErrorNotice } from '../components/WalletErrorNotice';
import { usePyramidaState, type PyramidaUiState } from '../hooks/usePyramidaState';
import { useBuyTicket } from '../hooks/useBuyTicket';
import { formatInt, formatTon } from '../lib/format';
import { CONTRACT_PARAMS, TON_NETWORK } from '../ton/config';
import { ticketNoun } from '../i18n/languages';
import { getIncomingReferrerKey } from '../ton/referral';
import { persistAttribution } from '../ton/attribution';
import { reportWebAuth, reportWebClick } from '../ton/track';
import { PartnerDashboardPage } from './PartnerDashboardPage';
import { usePartnerAuth, type PartnerAuth } from './usePartnerAuth';
import '../styles/partner.css';

const LEVELS = [1, 2, 3, 4, 5];

const REF_SHARE = CONTRACT_PARAMS.referralSharePercent / 100;

// Effective commission an inviter receives from a buyer `depth` levels below:
// the contract takes 20% of the *remaining* amount at every hop up the chain,
// so level d yields SHARE * (1 - SHARE)^(d-1) of the purchase.
function levelPct(depth: number): string {
  const pct = REF_SHARE * Math.pow(1 - REF_SHARE, depth - 1) * 100;
  return String(parseFloat(pct.toFixed(2)));
}

// Keep only digits and a single decimal separator; accept both '.' and ','.
function sanitizeQty(raw: string): string {
  const cleaned = raw.replace(/[^0-9.,]/g, '');
  const sepIndex = cleaned.search(/[.,]/);
  if (sepIndex === -1) return cleaned;
  return cleaned.slice(0, sepIndex + 1) + cleaned.slice(sepIndex + 1).replace(/[.,]/g, '');
}

export function PartnerApp() {
  const { t } = useTranslation();
  const walletAddress = useTonAddress();
  const [rememberDevice, setRememberDevice] = useState(false);
  const auth = usePartnerAuth(rememberDevice);
  const address = auth.address;
  const { state } = usePyramidaState(address || null);
  const invitedBy = useMemo(() => getIncomingReferrerKey(), []);

  // Web partner attribution for visitors landing on /partner?ref=<key>&sub=…
  // (a partner-to-partner invite): record the landing on load and link the
  // wallet on connect. No-op inside Telegram / without the proxy backend.
  useEffect(() => {
    persistAttribution();
    void reportWebClick();
  }, []);
  useEffect(() => {
    if (address) void reportWebAuth(address);
  }, [address]);

  const me = state.me;

  // Latch the registered partner identity so a transient chain-read failure
  // (rate-limited RPC) doesn't unmount the panel and lose form state.
  const [partnerMe, setPartnerMe] = useState<{
    key: number;
    referralsCount: number;
    referralEarnings: number;
  } | null>(null);
  useEffect(() => setPartnerMe(null), [address]);
  useEffect(() => {
    if (me?.registered && me.key != null) {
      setPartnerMe({
        key: me.key,
        referralsCount: me.referralsCount,
        referralEarnings: me.referralEarnings,
      });
    }
  }, [me]);

  // Prefer the fresh on-chain read for the partner identity, falling back to
  // the latched copy so a transient chain-read failure (rate-limited RPC)
  // doesn't drop us out of the dashboard. Using `me` directly also avoids a
  // one-frame flash of the landing right after a purchase registers.
  const identity = useMemo(
    () =>
      me?.registered && me.key != null
        ? {
            key: me.key,
            referralsCount: me.referralsCount,
            referralEarnings: me.referralEarnings,
          }
        : partnerMe,
    [me, partnerMe],
  );
  const isPartner = Boolean(address && identity);
  // A verified session (signed ton_proof) is what unlocks the panel; base the
  // hero/bottom CTAs on it so a connected-but-unsigned wallet is prompted to
  // sign in one action rather than being sent to a separate verify step.
  const verified = Boolean(auth.token);

  // Single connect-and-sign action shared by every CTA on the page. Carries the
  // "remember this device" choice so the visitor controls session length before
  // the wallet ever opens.
  const connect = () => void auth.login(rememberDevice);

  // "Remember this device" opt-in, shown next to every connect CTA (before a
  // wallet is attached) so the choice is always visible — not hidden behind a
  // verify step that a one-tap connect-and-sign skips over.
  const rememberToggle = (
    <label className="pl-remember">
      <input
        type="checkbox"
        checked={rememberDevice}
        onChange={(e) => setRememberDevice(e.target.checked)}
      />
      <span>{t('partner.auth.remember')}</span>
    </label>
  );

  // Signed in AND a participant → full re-render into the standalone
  // professional dashboard (no marketing landing / embedded widgets).
  if (verified && isPartner && identity) {
    return (
      <PartnerDashboardPage
        refKey={identity.key}
        referralsCount={identity.referralsCount}
        referralEarnings={identity.referralEarnings}
        auth={auth}
      />
    );
  }

  // Wallet connected but not through the gate yet → a focused single-screen
  // gate instead of the full marketing landing. Three cases it covers:
  //   • still reading the chain              → loading
  //   • connected but hasn't paid            → mini-landing to buy the first ticket
  //   • paid, but the session expired (401)  → re-authorize prompt
  // A paid participant who simply never signed in on this device (e.g. a
  // lottery player discovering the program) falls through to the full marketing
  // landing below, whose connect CTA signs them in.
  if (address && (me === null || !me.registered || auth.expired)) {
    return (
      <PartnerGate
        me={me}
        state={state}
        invitedBy={invitedBy}
        auth={auth}
        connect={connect}
        rememberToggle={rememberToggle}
      />
    );
  }

  return (
    <div className="pl-page">
      <header className={`pl-header${address ? ' pl-header--wallet' : ''}`}>
        <a className="pl-header__brand" href="/" aria-label="Pyramida">
          <PyramidSpinner className="pl-brand-pyramid" />
          <img src="/text-logo.webp" alt="PRMD" className="pl-header__logo" />
          <span className="pl-header__tag">
            <LayoutDashboard size={15} aria-hidden="true" />
            {t('partner.hero.badge')}
          </span>
          {TON_NETWORK !== 'mainnet' && <span className="pl-header__net">{TON_NETWORK}</span>}
        </a>
        <div className="pl-header__actions">
          <LanguageSwitcher />
          {/* Only show the wallet control once connected (to expose disconnect);
              while disconnected the gold hero CTA is the single connect action,
              so we don't duplicate it with TON Connect's own blue button. */}
          {walletAddress && <TonConnectButton className="ton-connect-compact" />}
        </div>
      </header>

      <main className="pl-main">
        {invitedBy && (
          <div className="pl-invited">
            <Handshake size={16} aria-hidden="true" />
            {t('partner.invited', { key: invitedBy })}
          </div>
        )}

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="pl-hero">
          <div className="pl-hero__glow" aria-hidden="true" />
          <span className="pl-badge">
            <Zap size={14} aria-hidden="true" /> {t('partner.hero.badge')}
          </span>
          <h1 className="pl-hero__title">
            {t('partner.hero.title1')} <span className="pl-gold">{t('partner.hero.title2')}</span>
          </h1>
          <p className="pl-hero__subtitle">{t('partner.hero.subtitle')}</p>
          <div className="pl-hero__cta">
            <button type="button" className="pl-btn pl-btn--gold pl-btn--lg" onClick={connect}>
              <Wallet size={17} aria-hidden="true" /> {t('partner.hero.ctaConnect')}
            </button>
          </div>
          {rememberToggle}
          <p className="pl-hero__note">{t('partner.hero.note')}</p>

          <div className="pl-live" aria-label={t('partner.live.title')}>
            <div className="pl-live__item">
              <span className="pl-live__value">{formatTon(state.lottery.prizePool)} GRAM</span>
              <span className="pl-live__label">{t('partner.live.pool')}</span>
            </div>
            <div className="pl-live__item">
              <span className="pl-live__value">{formatInt(state.system.totalUsers)}</span>
              <span className="pl-live__label">{t('partner.live.players')}</span>
            </div>
            <div className="pl-live__item">
              <span className="pl-live__value">{formatTon(state.lottery.totalWinsAmount)} GRAM</span>
              <span className="pl-live__label">{t('partner.live.paid')}</span>
            </div>
            <div className="pl-live__item">
              <span className="pl-live__value">20%</span>
              <span className="pl-live__label">{t('partner.live.levels')}</span>
            </div>
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────────────── */}
        <section className="pl-section">
          <h2 className="pl-h2">{t('partner.how.title')}</h2>
          <div className="pl-grid pl-grid--3">
            {(
              [
                [Ticket, 's1t', 's1d'],
                [Link2, 's2t', 's2d'],
                [Rocket, 's3t', 's3d'],
              ] as const
            ).map(([Icon, title, desc]) => (
              <div key={title} className="pl-card pl-step">
                <Icon size={22} aria-hidden="true" />
                <h3 className="pl-h3">{t(`partner.how.${title}`)}</h3>
                <p className="pl-muted">{t(`partner.how.${desc}`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Features ─────────────────────────────────────────────────── */}
        <section className="pl-section">
          <h2 className="pl-h2">{t('partner.features.title')}</h2>
          <div className="pl-grid pl-grid--3">
            {(
              [
                [Zap, 'f1'],
                [Layers, 'f2'],
                [Webhook, 'f3'],
                [Link2, 'f4'],
                [BarChart3, 'f5'],
                [Handshake, 'f6'],
                [Code2, 'f7'],
                [QrCode, 'f8'],
                [Send, 'f9'],
              ] as const
            ).map(([Icon, key]) => (
              <div key={key} className="pl-card pl-feature">
                <span className="pl-feature__icon">
                  <Icon size={20} aria-hidden="true" />
                </span>
                <h3 className="pl-h3">{t(`partner.features.${key}t`)}</h3>
                <p className="pl-muted">{t(`partner.features.${key}d`)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Commission levels ────────────────────────────────────────── */}
        <section className="pl-section">
          <h2 className="pl-h2">{t('partner.levels.title')}</h2>
          <p className="pl-muted pl-levels__desc">{t('partner.levels.desc')}</p>
          <div className="pl-levels">
            {LEVELS.map((n) => (
              <div key={n} className="pl-level" style={{ opacity: 1 - (n - 1) * 0.13 }}>
                <span className="pl-level__n">{t('partner.levels.level', { n })}</span>
                <span className="pl-level__share">{t('partner.levels.share', { pct: levelPct(n) })}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="pl-section">
          <h2 className="pl-h2">{t('partner.faq.title')}</h2>
          <div className="pl-faq">
            {[1, 2, 3, 4].map((n) => (
              <details key={n} className="pl-faq__item">
                <summary>
                  {t(`partner.faq.q${n}`)}
                  <ChevronDown size={16} aria-hidden="true" />
                </summary>
                <p className="pl-muted">{t(`partner.faq.a${n}`)}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Bottom CTA ───────────────────────────────────────────────── */}
        <section className="pl-cta">
          <h2 className="pl-h2">{t('partner.cta.title')}</h2>
          <p className="pl-muted">{t('partner.cta.subtitle')}</p>
          <button type="button" className="pl-btn pl-btn--gold pl-btn--lg" onClick={connect}>
            <Wallet size={17} aria-hidden="true" /> {t('partner.hero.ctaConnect')}
          </button>
          {rememberToggle}
        </section>

        <footer className="pl-footer">
          <a href="/">{t('partner.backToApp')}</a>
          <LegalLinks className="legal-links--partner" />
        </footer>
      </main>
    </div>
  );
}

/**
 * Single-screen unlock gate shown once a wallet is connected but the visitor
 * hasn't unlocked the panel yet. Renders exactly one of:
 *   • a chain-read spinner (still resolving `me`),
 *   • a compact "buy your first ticket" mini-landing (connected, not paid),
 *   • a "session expired — sign in again" prompt (paid, token rejected 401).
 * A paid participant who simply never signed in on this device is handled by
 * the caller, which shows the full marketing landing instead.
 */
function PartnerGate({
  me,
  state,
  invitedBy,
  auth,
  connect,
  rememberToggle,
}: {
  me: PyramidaUiState['me'];
  state: PyramidaUiState;
  invitedBy: number | null;
  auth: PartnerAuth;
  connect: () => void;
  rememberToggle: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="pl-page pl-gatepage">
      {/* The gate only renders with a wallet attached → always the stacked
          narrow-screen header layout. */}
      <header className="pl-header pl-header--wallet">
        <a className="pl-header__brand" href="/" aria-label="Pyramida">
          <PyramidSpinner className="pl-brand-pyramid" />
          <img src="/text-logo.webp" alt="PRMD" className="pl-header__logo" />
          <span className="pl-header__tag">
            <LayoutDashboard size={15} aria-hidden="true" />
            {t('partner.hero.badge')}
          </span>
          {TON_NETWORK !== 'mainnet' && <span className="pl-header__net">{TON_NETWORK}</span>}
        </a>
        <div className="pl-header__actions">
          <LanguageSwitcher />
          <TonConnectButton className="ton-connect-compact" />
        </div>
      </header>

      <main className="pl-gate">
        <section className="pl-gate__card">
          <div className="pl-hero__glow" aria-hidden="true" />
          {invitedBy != null && (
            <div className="pl-invited">
              <Handshake size={16} aria-hidden="true" />
              {t('partner.invited', { key: invitedBy })}
            </div>
          )}
          <span className="pl-badge">
            <Zap size={14} aria-hidden="true" /> {t('partner.hero.badge')}
          </span>

          {me === null ? (
            <div className="pl-gate__loading">
              <Loader2 className="spin" size={26} aria-hidden="true" />
              <p className="pl-muted">{t('partner.gate.checking')}</p>
            </div>
          ) : !me.registered ? (
            <>
              <Ticket size={30} aria-hidden="true" />
              <h1 className="pl-gate__title">{t('partner.gate.title')}</h1>
              <p className="pl-gate__text">
                {t('partner.gate.text', { price: formatTon(state.lottery.ticketPrice) })}
              </p>
              <PartnerBuy invitedBy={invitedBy} state={state} />
              <ul className="pl-gate__perks">
                {['perk1', 'perk2', 'perk3'].map((k) => (
                  <li key={k}>
                    <Check size={14} aria-hidden="true" /> {t(`partner.gate.${k}`)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <ShieldCheck size={30} aria-hidden="true" />
              <h1 className="pl-gate__title">{t('partner.gate.expiredTitle')}</h1>
              <p className="pl-gate__text">{t('partner.gate.expiredText')}</p>
              <button
                type="button"
                className="pl-btn pl-btn--gold pl-btn--lg"
                onClick={connect}
                disabled={auth.verifying}
              >
                <Wallet size={16} aria-hidden="true" />
                {auth.verifying ? t('partner.auth.verifying') : t('partner.gate.reauth')}
              </button>
              {rememberToggle}
              {auth.error && (
                <WalletErrorNotice message={t('partner.auth.error')} compact />
              )}
            </>
          )}

          <div className="pl-gate__pool">
            <span className="pl-gate__poolval">{formatTon(state.lottery.prizePool)} GRAM</span>
            <span className="pl-gate__poollabel">{t('partner.live.pool')}</span>
          </div>
        </section>

        <footer className="pl-footer">
          <a href="/">{t('partner.backToApp')}</a>
          <LegalLinks className="legal-links--partner" />
        </footer>
      </main>
    </div>
  );
}

/**
 * Inline ticket purchase on the partner page: lets a connected-but-not-yet-
 * registered visitor buy their first ticket right here (carrying the incoming
 * `?ref=` inviter key) instead of being bounced to the main app. Once the buy
 * is indexed, the polled on-chain state flips `me.registered` and the parent
 * swaps this eligibility card for the partner dashboard.
 */
function PartnerBuy({
  invitedBy,
  state,
}: {
  invitedBy: number | null;
  state: PyramidaUiState;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const { buyTicket, pending, error, lastTxAt } = useBuyTicket();
  const [qtyInput, setQtyInput] = useState('1');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (lastTxAt !== null) setSent(true);
  }, [lastTxAt]);

  const ticketPrice = state.lottery.ticketPrice;
  const minQty = ticketPrice > 0 ? CONTRACT_PARAMS.minEntryTon / ticketPrice : 1;
  const qty = parseFloat(qtyInput.replace(',', '.'));
  const totalTon = Number.isFinite(qty) ? Math.round(qty * ticketPrice * 1e9) / 1e9 : NaN;
  const qtyValid = Number.isFinite(qty) && totalTon >= CONTRACT_PARAMS.minEntryTon - 1e-9;

  const stepQty = (delta: number) => {
    const base = Number.isFinite(qty) ? qty : minQty;
    const next = Math.max(minQty, Math.round((base + delta) * 100) / 100);
    setQtyInput(String(next).replace('.', ','));
  };

  const buyLabel = !qtyValid
    ? t('lottery.buyMin', { amount: formatTon(CONTRACT_PARAMS.minEntryTon) })
    : t('lottery.buy', {
        qty: formatTon(qty),
        word: ticketNoun(lang, qty),
        total: formatTon(totalTon),
      });

  return (
    <div className="pl-buy">
      <div className="pl-buy__qty">
        <button
          type="button"
          className="pl-buy__step"
          onClick={() => stepQty(-1)}
          disabled={pending || (Number.isFinite(qty) && qty <= minQty)}
          aria-label={t('lottery.decrease')}
        >
          −
        </button>
        <input
          className="pl-buy__input"
          type="text"
          inputMode="decimal"
          value={qtyInput}
          onChange={(e) => setQtyInput(sanitizeQty(e.target.value))}
          disabled={pending}
          aria-label={t('lottery.ticketCount')}
        />
        <button
          type="button"
          className="pl-buy__step"
          onClick={() => stepQty(1)}
          disabled={pending}
          aria-label={t('lottery.increase')}
        >
          +
        </button>
      </div>
      <button
        type="button"
        className="pl-btn pl-btn--gold"
        disabled={pending || (!!address && !qtyValid)}
        onClick={() => {
          if (!address) {
            tonConnectUI.openModal();
            return;
          }
          void buyTicket(invitedBy, totalTon);
        }}
      >
        {pending ? (
          <>
            <Loader2 className="spin" size={16} aria-hidden="true" /> {t('lottery.sending')}
          </>
        ) : (
          <>
            <Ticket size={16} aria-hidden="true" /> {buyLabel}
          </>
        )}
      </button>
      {error && <WalletErrorNotice message={error} compact />}
      {pending && <p className="pl-muted pl-buy__status">{t('lottery.confirmInWallet')}</p>}
      {!pending && sent && <p className="pl-muted pl-buy__status">{t('lottery.buySent')}</p>}
    </div>
  );
}
